<template>
  <div>
    <span class="primary--text" v-if="!_retrying">
      Only upload the two Scan Gauge Scans of a single arch.
    </span>
    <span class="primary--text" v-if="_retrying">
      Attempt {{ retryCount + 1 }} - Upload at least one new scan.
    </span>

    <div v-if="_retrying && faceCount" class="py-2">
      Expected Gauge Selection Count: <strong> {{ faceCount }} </strong>
      <v-btn
        small
        @click="resetForDentate(dentate, $vuetify.breakpoint.lgAndUp)"
        class="ml-3 elevation-0 rounded-10"
      >
        Restart
      </v-btn>
    </div>

    <v-data-table
      v-if="_retrying"
      :headers="[
        { text: 'Attempt', value: 'attempt' },
        { text: 'Scan 1', value: 'scan1' },
        { text: 'Scan 2', value: 'scan2' }
      ]"
      :items="uploadedScans"
      disable-sort
      hide-default-footer
    />
    <v-card flat>
      <v-card-text class="px-0 pt-3">
        <v-form ref="form">
          <v-row>
            <v-col cols="12" lg="6" class="pb-0">
              <ScanUploader categoryTitle="Left to Right" v-model="scan1File" />
              <v-file-input
                v-model="scan1File"
                :rules="fileRules"
                label="Left-To-Right Scan"
                class="rounded-10 mt-2"
                dense
                outlined
                truncate-length="25"
                :disabled="!!bypass"
              />
            </v-col>
            <v-col cols="12" lg="6" class="pb-0">
              <ScanUploader categoryTitle="Right to Left" v-model="scan2File" />
              <v-file-input
                v-model="scan2File"
                :rules="fileRules"
                label="Right-To-Left Scan"
                class="rounded-10 mt-2"
                dense
                outlined
                truncate-length="25"
                :disabled="!!bypass"
              />
            </v-col>

            <v-col cols="12" class="pt-0 ms-2">
              <v-checkbox
                v-model="_narrowScanBodiesPresent"
                label="Include Narrow Scan Bodies"
                dense
                class="mt-0 pt-0"
              />
            </v-col>
          </v-row>
          <div class="d-flex">
            <v-btn
              class="btn-primary"
              @click="handleContinue"
              :disabled="
                loading ||
                (!scansChanged && uploadedScans.length === retryCount)
              "
            >
              <span> Continue </span>
            </v-btn>
            <v-btn
              v-if="
                uploadedScans.length &&
                _retrying &&
                !scansChanged &&
                uploadedScans.length === retryCount
              "
              class="elevation-0 rounded-10 ml-3"
              @click="reupload"
            >
              Re-upload Scans
            </v-btn>
            <v-scale-transition origin="center center">
              <div class="flex-grow-1 ml-3 mr-1" v-if="loading">
                <v-progress-linear
                  rounded
                  indeterminate
                  color="primary"
                  height="36"
                  class="rounded-10"
                  background-opacity="0.6"
                >
                  <span
                    class="text-body-2 font-weight-medium white--text loading-pulse"
                  >
                    UPLOADING...
                  </span>
                </v-progress-linear>
              </div>
            </v-scale-transition>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="uploading"
      max-width="600"
      persistent
      content-class="rounded-10"
    >
      <v-card class="card-bg rounded-10" v-if="uploading">
        <v-card-text class="text-center pt-5">
          <v-row align="center" justify="center">
            <v-col cols="12" class="pa-5">
              <v-progress-circular
                :rotate="-90"
                :width="8"
                :size="200"
                :value="uploadProgress"
                color="primary"
              >
                <span class="loading-pulse">
                  Uploading Files...
                  <br />
                  {{ uploadProgress }}%
                </span>
              </v-progress-circular>
            </v-col>
          </v-row>
          <p :class="['mt-5']">
            Please wait for your uploads to complete before continuing.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { getTextColour, toTitleCase } from "@/utils"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import apiClient from "@/lib/ApiClient"
import ScanUploader from "./UploadScans/ScanUploader.vue"

export default {
  name: "UploadScans",

  components: { ScanUploader },

  props: ["prop"],

  data() {
    return {
      getTextColour,
      loading: false,
      scan1File: null,
      scan2File: null,
      uploadedScan1: null,
      uploadedScan2: null,
      uploadProgress: 0,
      failedUploads: [],
      currentUpload: 0,
      uploading: false,
      fileRules: [
        (file) => {
          if (file) {
            const { name } = file
            const ext = name.split(".").pop().toUpperCase()
            if (ext === "STL" || ext === "PLY") {
              return true
            }
            return "File must be an STL or PLY."
          }
          return "You must select a file."
        }
      ]
    }
  },

  computed: {
    ...mapGetters([
      "accessToken",
      "orderDetails",
      "orderUid",
      "country",
      "upperUploadedScans",
      "lowerUploadedScans",
      "narrowScanBodiesPresent",
      "faceCount",
      "retryCount",
      "retrying"
    ]),

    _retrying() {
      return this.retrying === this.dentate
    },

    _narrowScanBodiesPresent: {
      get() {
        return this.narrowScanBodiesPresent
      },

      set(value) {
        this.setNarrowScanBodiesPresent(value)
      }
    },

    dentate() {
      return this.prop || this.orderDetails.dentate
    },

    uploadedScans() {
      return this[`${this.dentate}UploadedScans`] || []
    },

    scansChanged() {
      if (!this._retrying) return true
      if (this.scan1File === null || this.scan2File === null) return true
      return !(
        this.uploadedScan1?.file === this.scan1File &&
        this.uploadedScan2?.file === this.scan2File
      )
    },

    bypass() {
      return (
        (!this._retrying && this.uploadedScans.length) ||
        this.uploadedScans.length === this.retryCount + 1
      )
    }
  },

  methods: {
    ...mapActions([
      "setOrderDetails",
      "setUpperUploadedScans",
      "setLowerUploadedScans",
      "setNarrowScanBodiesPresent",
      "setUpperAddScansResponse",
      "setLowerAddScansResponse",
      "setTransition",
      "setErrorMessage",
      "resetForDentate"
    ]),

    setUploadedScans(payload) {
      this[`set${toTitleCase(this.dentate)}UploadedScans`](payload)
    },

    async handleContinue() {
      if (!this.$refs.form.validate()) return
      if (this.bypass) return this.$emit("next")

      try {
        this.loading = true

        if (!this.orderDetails[`${this.dentate}_options`].nc_session_id) {
          const response = await NCFrontend.ApiClient.sessionCreate(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            `Bearer ${this.accessToken}`
          )
          this.setOrderDetails({
            ...this.orderDetails,
            [`${this.dentate}_options`]: {
              ...this.orderDetails[`${this.dentate}_options`],
              nc_session_id: response.sessionId
            }
          })
        }

        await this.uploadScans()

        this[`set${toTitleCase(this.dentate)}AddScansResponse`](
          await NCFrontend.ApiClient.sessionAddScans(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            `Bearer ${this.accessToken}`,
            {
              sessionId:
                this.orderDetails[`${this.dentate}_options`].nc_session_id,
              scan1FileUrl: this.uploadedScan1.url,
              scan1FileOftUid: this.uploadedScan1.oftUid,
              scan2FileUrl: this.uploadedScan2.url,
              scan2FileOftUid: this.uploadedScan2.oftUid,
              narrowScanBodiesPresent: this.narrowScanBodiesPresent,
              retrying: this._retrying
            }
          )
        )

        this.setUploadedScans([
          ...this.uploadedScans,
          {
            attempt: this.uploadedScans.length + 1,
            scan1: this.scan1File.name,
            scan2: this.scan2File.name
          }
        ])

        this.setTransition(true)
        this.$emit("next")
      } catch (err) {
        if (err instanceof NCFrontend.ApiClient.ErrorResponse) {
          switch (err.type) {
            case NCFrontend.ApiClient.ErrorType.IdenticalScanFiles:
              this.setErrorMessage("Identical scans detected.")
              break
            case NCFrontend.ApiClient.ErrorType.ResourceNotFound:
              this.setErrorMessage("Failed to find session.")
              break
            case NCFrontend.ApiClient.ErrorType.MaxValidationRetriesReached:
              this.setErrorMessage("Max retry attempts reached.")
              break
            case NCFrontend.ApiClient.ErrorType.ScansAlreadyUsedInValidation:
              // this.uploadedScan1 = this.scan1File
              // this.uploadedScan2 = this.scan2File
              this.setErrorMessage(
                "Scans have already been used in an attempt."
              )
              break
            case NCFrontend.ApiClient.ErrorType.ServerError:
              this.setErrorMessage("An internal server error occurred.")
              break
          }
        } else this.setErrorMessage("An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    reupload() {
      this.scan1File = null
      this.scan2File = null
    },

    async uploadScans() {
      this.uploading = true

      try {
        const filesToUpload = []
        if (this.uploadedScan1?.file !== this.scan1File) {
          filesToUpload.push(this.scan1File)
        }

        if (this.uploadedScan2?.file !== this.scan2File) {
          filesToUpload.push(this.scan2File)
        }

        if (filesToUpload.length) await this.handleFileUpload(filesToUpload)
      } catch (err) {
        this.setErrorMessage("Failed to upload scans")
      } finally {
        this.uploading = false
        this.uploadProgress = 0
      }
    },

    async handleFileUpload(files) {
      const uploadedFiles = await apiClient.oft.uploadFiles(
        files,
        (progressValue) => {
          this.uploadProgress = progressValue
        }
      )

      await apiClient.oft.getFileUrls(uploadedFiles)

      if (this.uploadedScan1?.file !== this.scan1File) {
        this.uploadedScan1 = uploadedFiles.find(
          (file) => file.file === this.scan1File
        )
      }
      if (this.uploadedScan2?.file !== this.scan2File) {
        this.uploadedScan2 = uploadedFiles.find(
          (file) => file.file === this.scan2File
        )
      }

      const payload = []

      uploadedFiles.forEach((file) => {
        if (file.uploaded) {
          payload.push({
            file_name: file.oftFileName,
            file_category: "scan_gauge_scan",
            file_type: "scan",
            oft_file_uid: file.oftUid
          })
        } else this.failedUploads.push(file)
      })

      await apiClient.instance.orders.addFiles(
        this.orderUid,
        this.orderDetails[`${this.dentate}_options`].order_session_uid,
        payload
      )
    }
  }
}
</script>
